import React, { useCallback, useEffect, useRef, useState } from 'react';
import { API_ENDPOINTS } from '../../config';
import { getBearerAuthHeaders } from '../../utils/authFetchHeaders';
import { snaptradeConnectLink, snaptradeSync } from '../../services/vectorDashboardApi';
import TradeIntegrationModal from '../vectorDashboard/TradeIntegrationModal';
import AlpacaNakedCard from './AlpacaNakedCard';
import { COMING_SOON_BROKERS } from '../welcome/connectSources';
import './TradeWatcherConnections.css';

/**
 * Trade Watcher → Connections: which accounts are on the board.
 * A tile per connected account (from My Portfolio) with an Add to board /
 * On board button, and a tile per broker that can still be connected (Schwab,
 * Robinhood… each its own). "More info" opens a pop-up with the details.
 * Accounts connected from here go on the board straight away.
 */

// Before leaving for a broker's sign-in page (or opening the connect window):
// the accounts that already exist, so the new ones can be switched on after.
const PENDING_KEY = 'riskloTwConnectPending';
const PENDING_MAX_AGE_MS = 30 * 60 * 1000;

const headers = () => ({ 'Content-Type': 'application/json', ...getBearerAuthHeaders() });
const accountIds = (brokers) => (brokers || []).flatMap((b) => (b.accounts || []).map((a) => `${b.id}|${a.accountKey}`));

function readPending() {
  try {
    const p = JSON.parse(sessionStorage.getItem(PENDING_KEY) || 'null');
    return p && Date.now() - p.at < PENDING_MAX_AGE_MS ? p : null;
  } catch (_e) { return null; }
}
function writePending(brokers) {
  try { sessionStorage.setItem(PENDING_KEY, JSON.stringify({ at: Date.now(), known: accountIds(brokers) })); } catch (_e) { /* fine */ }
}
function clearPending() {
  try { sessionStorage.removeItem(PENDING_KEY); } catch (_e) { /* fine */ }
}

function ago(iso) {
  if (!iso) return '';
  const m = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  return h < 24 ? `${h}h ago` : `${Math.round(h / 24)}d ago`;
}

const LOGOS = {
  'ninjatrader-addon': '/brokers/ninjatrader.jpg',
  alpaca: '/brokers/alpaca.png',
  tradovate: '/brokers/ninjatrader.jpg',
  coinbase: '/brokers/coinbase.webp',
  ibkr: '/brokers/ibkr.png',
};
const SUBTITLE = {
  alpaca: 'Alpaca · stocks',
  tradovate: 'Futures · no add-on needed',
  snaptrade: 'Linked through SnapTrade',
  coinbase: 'Coinbase · crypto',
};

const norm = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]/g, '');

/** The popular brokerage a linked account belongs to ("Schwab" → Charles Schwab), if any. */
function brokerageOf(account) {
  const inst = norm(account.institution || String(account.name || '').split(' · ')[0]);
  if (!inst) return null;
  return COMING_SOON_BROKERS.find((b) => {
    const t = norm(b.title);
    const sl = norm(b.slug);
    return t.includes(inst) || inst.includes(sl) || sl.includes(inst);
  }) || null;
}

function Logo({ src, name, glyph = null, size = 'lg' }) {
  return (
    <span className={`twc-logo twc-logo--${size}`} aria-hidden="true">
      {src ? <img src={src} alt="" loading="lazy" /> : <span>{glyph || String(name || '?').charAt(0)}</span>}
    </span>
  );
}

/** The big, obvious on/off for one account. */
function BoardButton({ on, busy, name, onFlip }) {
  return (
    <button
      type="button"
      aria-pressed={on}
      aria-label={on ? `Remove ${name} from the board` : `Add ${name} to the board`}
      className={`twc-board-btn${on ? ' is-on' : ''}`}
      onClick={onFlip}
      disabled={busy}
    >
      {busy ? (on ? 'Removing…' : 'Adding…') : on ? (
        <>
          <span className="twc-board-check" aria-hidden="true">✓</span>
          <span className="twc-board-on">On board</span>
          <span className="twc-board-off">Remove</span>
        </>
      ) : '+ Add to board'}
    </button>
  );
}

function StatusPill({ tone, children }) {
  return <span className={`twc-pill${tone ? ` is-${tone}` : ''}`}>{children}</span>;
}

/** One square: logo, name, status, the main action and "More info". */
function Tile({ logo, glyph = null, name, subtitle, status, action, onInfo, muted = false }) {
  return (
    <article className={`twc-tile${muted ? ' is-muted' : ''}`}>
      <div className="twc-tile-top">
        <Logo src={logo} name={name} glyph={glyph} />
        {status}
      </div>
      <div className="twc-tile-text">
        <h4 title={name}>{name}</h4>
        {subtitle ? <p className="twc-muted">{subtitle}</p> : null}
      </div>
      <div className="twc-tile-actions">
        {action}
        {onInfo ? <button type="button" className="twc-info-btn" onClick={onInfo}>More info</button> : null}
      </div>
    </article>
  );
}

/** The "More info" pop-up. */
function InfoModal({ info, onClose, wide = false, children }) {
  const closeRef = useRef(null);
  useEffect(() => {
    closeRef.current?.focus();
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div className="twc-modal-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className={`twc-modal${wide ? ' twc-modal--wide' : ''}`} role="dialog" aria-modal="true" aria-labelledby="twc-modal-title">
        <header className="twc-modal-head">
          <Logo src={info.logo} name={info.name} />
          <div>
            <h3 id="twc-modal-title">{info.name}</h3>
            {info.subtitle ? <p className="twc-muted">{info.subtitle}</p> : null}
          </div>
          <button type="button" ref={closeRef} className="twc-modal-close" onClick={onClose} aria-label="Close">×</button>
        </header>
        {info.note ? <p className="twc-modal-note">{info.note}</p> : null}
        {info.facts?.length ? (
          <dl className="twc-facts">
            {info.facts.map(([k, v]) => <div key={k}><dt>{k}</dt><dd>{v}</dd></div>)}
          </dl>
        ) : null}
        {children}
      </div>
    </div>
  );
}

/**
 * addonSetup: the NinjaTrader add-on's install steps and feed URL (rendered by
 * the page), shown in a pop-up from the add-on tile.
 */
export default function TradeWatcherConnections({ returnInfo = null, addonAccounts = 0, onBoardChanged, addonSetup = null }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busyKey, setBusyKey] = useState('');
  const [modal, setModal] = useState({ open: false, broker: null });
  const [infoKey, setInfoKey] = useState(null);
  const [opening, setOpening] = useState('');
  const dataRef = useRef(null);

  const load = useCallback(async () => {
    const res = await fetch(API_ENDPOINTS.tradeWatcherConnections, { headers: headers() });
    const d = await res.json().catch(() => ({}));
    if (!res.ok || !d.success) throw new Error(d.error || 'Couldn’t load your connections');
    setData(d);
    dataRef.current = d;
    return d;
  }, []);

  const setEnabled = useCallback(async (brokerId, accountKey, enabled) => {
    const res = await fetch(API_ENDPOINTS.tradeWatcherConnections, {
      method: 'PUT', headers: headers(), body: JSON.stringify({ broker: brokerId, accountKey, enabled }),
    });
    const d = await res.json().catch(() => ({}));
    if (!res.ok || !d.success) throw new Error(d.error || 'Couldn’t update');
    return d;
  }, []);

  /** Switch on accounts that weren't there before the person started connecting. */
  const enableNew = useCallback(async () => {
    const pending = readPending();
    if (!pending) return 0;
    const d = await load();
    const known = new Set(pending.known || []);
    const fresh = (d.brokers || []).filter((b) => b.live).flatMap((b) => (b.accounts || [])
      .filter((a) => !known.has(`${b.id}|${a.accountKey}`) && !a.enabled)
      .map((a) => ({ broker: b.id, accountKey: a.accountKey })));
    if (!fresh.length) return 0;
    for (const f of fresh) {
      // eslint-disable-next-line no-await-in-loop
      await setEnabled(f.broker, f.accountKey, true).catch(() => {});
    }
    clearPending();
    await load();
    onBoardChanged?.();
    return fresh.length;
  }, [load, setEnabled, onBoardChanged]);

  useEffect(() => {
    load().catch((e) => setError(e.message));
  }, [load]);

  // Back from SnapTrade's or Tradovate's sign-in page.
  useEffect(() => {
    if (!returnInfo) return undefined;
    let cancelled = false;
    let timer = null;
    if (returnInfo.failed) {
      clearPending();
      if (!returnInfo.abandoned) setError('The connection didn’t finish. Try connecting again.');
      return undefined;
    }
    const plural = (n) => `${n} account${n === 1 ? '' : 's'}`;
    if (returnInfo.kind === 'snaptrade') {
      setNotice('Brokerage linked. Pulling in your accounts…');
      snaptradeSync()
        .catch(() => null)
        .then(() => (cancelled ? 0 : enableNew()))
        .then((n) => { if (!cancelled) setNotice(n ? `Brokerage linked. ${plural(n)} added to your board.` : 'Brokerage linked. Add the accounts you want on your board.'); })
        .catch((e) => { if (!cancelled) { setNotice(''); setError(e.message); } });
    } else if (returnInfo.kind === 'tradovate') {
      // Tradovate's accounts arrive with the sync that started on its sign-in.
      setNotice('NinjaTrader / Tradovate connected. Waiting for your accounts…');
      let tries = 0;
      const poll = async () => {
        if (cancelled) return;
        tries += 1;
        const n = await enableNew().catch(() => 0);
        if (cancelled) return;
        if (n) { setNotice(`NinjaTrader / Tradovate connected. ${plural(n)} added to your board.`); return; }
        if (tries >= 24) { setNotice('NinjaTrader / Tradovate connected. Your accounts show here once the first sync finishes.'); return; }
        timer = setTimeout(poll, 5000);
      };
      poll();
    }
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [returnInfo, enableNew]);

  const toggle = async (broker, account, enabled) => {
    const key = `${broker.id}|${account.accountKey}`;
    setBusyKey(key);
    setError('');
    try {
      const d = await setEnabled(broker.id, account.accountKey, enabled);
      setData((cur) => ({ ...cur, brokers: d.brokers }));
      onBoardChanged?.();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusyKey('');
    }
  };

  const openConnect = (connectId = null) => {
    writePending(dataRef.current?.brokers);
    setModal({ open: true, broker: connectId });
  };

  const closeModal = async () => {
    setModal({ open: false, broker: null });
    const n = await enableNew().catch(() => 0);
    if (n) setNotice(`${n} account${n === 1 ? '' : 's'} added to your board.`);
  };

  /** SnapTrade's sign-in for one brokerage (Schwab, Robinhood…), back to this tab. */
  const connectBrokerage = async (b) => {
    setOpening(b.slug);
    setError('');
    writePending(dataRef.current?.brokers);
    try {
      const light = document.body.classList.contains('light-mode');
      const res = await snaptradeConnectLink({ broker: b.slug, darkMode: !light, returnTo: 'trade-watcher' });
      if (!res?.url) throw new Error('No link');
      window.location.assign(res.url);
    } catch (_e) {
      setOpening('');
      setError(`Couldn’t open ${b.title}. Try again in a moment.`);
    }
  };

  const brokers = data?.brokers || [];
  const byId = Object.fromEntries(brokers.map((b) => [b.id, b]));
  const accounts = brokers.filter((b) => b.live && b.connected)
    .flatMap((b) => b.accounts.map((a) => ({ broker: b, account: a, key: `${b.id}|${a.accountKey}` })));
  const onBoard = accounts.filter((x) => x.account.enabled).length + addonAccounts;
  // Brokerages already linked show as accounts above, not again as Connect tiles.
  const linkedSlugs = new Set(accounts.filter((x) => x.broker.id === 'snaptrade')
    .map((x) => brokerageOf(x.account)?.slug).filter(Boolean));
  const accountLogo = (b, a) => a.logoUrl || (b.id === 'snaptrade' ? brokerageOf(a)?.logo : null) || LOGOS[b.id];
  const snapOk = Boolean(data?.snaptradeAvailable);
  const addonNote = 'RiskLo Tally runs inside NinjaTrader and reports every account live, with naked positions, connection status and auto-close.';

  // What "More info" shows, for whichever tile opened it.
  const info = (() => {
    if (!infoKey) return null;
    if (infoKey === 'addon') {
      return {
        name: 'NinjaTrader add-on',
        subtitle: addonAccounts ? `RiskLo Tally · ${addonAccounts} account${addonAccounts === 1 ? '' : 's'} reporting` : 'RiskLo Tally · set up in a few minutes',
        logo: LOGOS['ninjatrader-addon'],
        note: addonNote,
        wide: true,
        setup: true,
      };
    }
    if (infoKey.startsWith('st:')) {
      const b = COMING_SOON_BROKERS.find((x) => x.slug === infoKey.slice(3));
      if (!b) return null;
      return {
        name: b.title,
        subtitle: snapOk ? 'Linked through SnapTrade' : 'Coming soon',
        logo: b.logo,
        note: `Sign in to ${b.title} on its own page; RiskLo gets read-only access. Each account shows its value, open P/L, positions and whether every position has a protective stop. Some brokerages update only every few minutes.`,
        facts: [],
        footer: snapOk
          ? <button type="button" className="vd-btn" onClick={() => { setInfoKey(null); connectBrokerage(b); }}>Connect {b.title}</button>
          : null,
      };
    }
    if (infoKey.startsWith('broker:')) {
      const b = byId[infoKey.slice(7)];
      if (!b) return null;
      return {
        name: b.name, subtitle: b.live ? 'Not connected yet' : 'Not available live', logo: LOGOS[b.id], note: b.note,
        facts: b.connected && !b.live ? [['In My Portfolio', 'Connected']] : [],
        footer: b.live && b.id !== 'snaptrade'
          ? <button type="button" className="vd-btn" onClick={() => { setInfoKey(null); openConnect(b.connectId); }}>Connect {b.name}</button>
          : null,
      };
    }
    const x = accounts.find((a) => a.key === infoKey);
    if (!x) return null;
    const { broker: b, account: a } = x;
    return {
      name: a.name,
      subtitle: SUBTITLE[b.id] || b.name,
      logo: accountLogo(b, a),
      note: b.note,
      facts: [
        ['On your board', a.enabled ? 'Yes' : 'No'],
        ['Last updated', a.lastReadAt ? ago(a.lastReadAt) : a.enabled ? 'Not yet' : '—'],
        ...(a.isPaper ? [['Account type', 'Paper']] : []),
        ...(a.error ? [['Problem', a.error]] : []),
        ...(a.disabled ? [['Connection', 'Needs reconnecting in My Portfolio']] : []),
      ],
      broker: b,
      account: a,
    };
  })();

  const accountStatus = (a) => (a.error || a.disabled
    ? <StatusPill tone="warn">Needs attention</StatusPill>
    : a.enabled ? <StatusPill tone="on">On board</StatusPill> : <StatusPill>Off</StatusPill>);

  const addonTile = (
    <Tile
      key="addon"
      logo={LOGOS['ninjatrader-addon']}
      name="NinjaTrader add-on"
      subtitle={addonAccounts ? `${addonAccounts} account${addonAccounts === 1 ? '' : 's'} reporting` : 'Live NinjaTrader accounts'}
      status={addonAccounts ? <StatusPill tone="on">On board</StatusPill> : null}
      action={<button type="button" className={`twc-tile-btn${addonAccounts ? ' is-quiet' : ''}`} onClick={() => setInfoKey('addon')}>{addonAccounts ? 'Setup & feed URL' : 'Set up'}</button>}
    />
  );

  const directToAdd = ['alpaca', 'tradovate', 'coinbase'].map((id) => byId[id]).filter((b) => b && !b.connected);

  return (
    <div className="twc">
      <header className="twc-head">
        <div>
          <h3>Connections</h3>
          <p className="twc-muted">
            Pick the accounts you want watched live. RiskLo only reads them: it never places, changes or cancels an order.
          </p>
        </div>
        <button type="button" className="vd-btn" onClick={() => openConnect(null)}>+ Connect a broker</button>
      </header>

      {notice ? <p className="twc-notice" role="status">{notice}</p> : null}
      {error ? <p className="twc-error" role="alert">{error}</p> : null}
      {!data && !error ? <p className="twc-muted">Loading your connections…</p> : null}

      {data ? (
        <>
          <section className="twc-section">
            <h5 className="twc-section-title">
              Your accounts
              <span className="twc-count">{onBoard} on board</span>
            </h5>
            {accounts.length || addonAccounts ? (
              <div className="twc-grid">
                {addonAccounts ? addonTile : null}
                {accounts.map(({ broker: b, account: a, key }) => (
                  <Tile
                    key={key}
                    logo={accountLogo(b, a)}
                    name={a.name}
                    subtitle={SUBTITLE[b.id] || b.name}
                    status={accountStatus(a)}
                    action={<BoardButton on={a.enabled} busy={busyKey === key} name={a.name} onFlip={() => toggle(b, a, !a.enabled)} />}
                    onInfo={() => setInfoKey(key)}
                  />
                ))}
              </div>
            ) : (
              <p className="twc-empty">Nothing connected yet. Pick a broker below.</p>
            )}
          </section>

          <section className="twc-section">
            <h5 className="twc-section-title">Add a connection</h5>
            <div className="twc-grid">
              {addonAccounts ? null : addonTile}
              {directToAdd.map((b) => (
                <Tile
                  key={b.id}
                  logo={LOGOS[b.id]}
                  name={b.name}
                  subtitle={SUBTITLE[b.id]}
                  action={<button type="button" className="twc-tile-btn" onClick={() => openConnect(b.connectId)}>Connect</button>}
                  onInfo={() => setInfoKey(`broker:${b.id}`)}
                />
              ))}
              {COMING_SOON_BROKERS.filter((b) => !linkedSlugs.has(b.slug)).map((b) => (
                <Tile
                  key={b.slug}
                  logo={b.logo}
                  name={b.title}
                  subtitle={snapOk ? 'Stocks and options' : 'Coming soon'}
                  muted={!snapOk}
                  action={snapOk
                    ? <button type="button" className="twc-tile-btn" onClick={() => connectBrokerage(b)} disabled={Boolean(opening)}>{opening === b.slug ? 'Opening…' : 'Connect'}</button>
                    : <button type="button" className="twc-tile-btn is-quiet" disabled>Coming soon</button>}
                  onInfo={() => setInfoKey(`st:${b.slug}`)}
                />
              ))}
              {snapOk ? (
                <Tile
                  key="more"
                  logo={null}
                  glyph="+"
                  name="More brokerages"
                  subtitle="Dozens more through SnapTrade"
                  action={<button type="button" className="twc-tile-btn is-quiet" onClick={() => openConnect('snaptrade')}>Browse all</button>}
                />
              ) : null}
              {brokers.filter((b) => !b.live).map((b) => (
                <Tile
                  key={b.id}
                  logo={LOGOS[b.id]}
                  name={b.name}
                  subtitle={b.connected ? 'Connected · not live' : 'Not available live'}
                  muted
                  action={<button type="button" className="twc-tile-btn is-quiet" disabled>Not live</button>}
                  onInfo={() => setInfoKey(`broker:${b.id}`)}
                />
              ))}
            </div>
          </section>
        </>
      ) : null}

      {info ? (
        <InfoModal info={info} wide={Boolean(info.wide)} onClose={() => setInfoKey(null)}>
          {info.setup ? (
            <div className="twc-setup">
              {addonSetup || <p className="twc-muted">Ask your support team for the add-on and your feed URL.</p>}
            </div>
          ) : null}
          {info.account ? (
            <div className="twc-modal-actions">
              <BoardButton
                on={info.account.enabled}
                busy={busyKey === infoKey}
                name={info.name}
                onFlip={() => toggle(info.broker, info.account, !info.account.enabled)}
              />
            </div>
          ) : null}
          {info.broker?.id === 'alpaca' ? <div className="twc-modal-extra"><AlpacaNakedCard embedded /></div> : null}
          {info.footer ? <div className="twc-modal-actions">{info.footer}</div> : null}
        </InfoModal>
      ) : null}

      <TradeIntegrationModal
        isOpen={modal.open}
        initialBrokerId={modal.broker}
        returnPage="trade-watcher"
        onClose={closeModal}
        onSynced={() => { load().catch(() => {}); }}
      />
    </div>
  );
}
